import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/src/shadComponents/ui/form';
import { Input } from '@/src/shadComponents/ui/input';
import api from '@/src/services/api';

const formSchema = z
    .object({
        password: z.string().min(8, 'Password must be at least 8 characters'),
        confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords don't match",
        path: ['confirmPassword'],
    });

export default function PasswordReset() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [resetToken, setResetToken] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            password: '',
            confirmPassword: '',
        },
    });

    useEffect(() => {
        const validateToken = async () => {
            try {
                const response = await api.get(
                    `/password-reset/validate/${token}`,
                );
                setResetToken(response.data.resetToken);
            } catch (err) {
                setError('Invalid or expired reset link');
            }
        };

        if (token) {
            validateToken();
        }
    }, [token]);

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            await api.post('/password-reset/reset', {
                resetToken,
                newPassword: values.password,
            });
            navigate('/login', {
                state: {
                    message: 'Password successfully reset. Please log in.',
                },
            });
        } catch (err) {
            setError('Failed to reset password. Please try again.');
        }
    };

    if (error) {
        return (
            <div className="flex items-center h-screen justify-center dark:bg-[#262C2C] dark:text-white">
                <Card className="w-[90%] md:min-w-2/6 md:w-2/6 rounded-none shadow-none border-0">
                    <CardHeader>
                        <CardTitle className="plex-sans-bold text-2xl">
                            Error
                        </CardTitle>
                        <CardDescription className="plex-sans text-lg">
                            {error}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <p className="plex-sans text-lg">
                            If you continue to have issues, please contact
                            support@photoengineers.com.
                        </p>
                        <br />
                        <button
                            className="w-full black-button plex-sans-bold text-xl"
                            onClick={() => {
                                navigate('/login');
                            }}
                        >
                            Back to Login &rarr;
                        </button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <>
            <div className="flex items-center h-screen justify-center dark:bg-[#262C2C] dark:text-white">
                <Card className="w-[90%] md:min-w-2/6 md:w-2/6 rounded-none shadow-none border-0">
                    <CardHeader>
                        <CardTitle className="plex-sans-bold text-2xl">
                            Reset Password
                        </CardTitle>
                        <CardDescription className="plex-sans text-lg">
                            Enter your new password below.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Form {...form}>
                            <form
                                onSubmit={form.handleSubmit(onSubmit)}
                                className="space-y-4"
                            >
                                <FormField
                                    control={form.control}
                                    name="password"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="plex-sans-bold text-xl">
                                                New Password
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="password"
                                                    className="shadow-none monospace-font font-size-22 py-2"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="confirmPassword"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="plex-sans-bold text-xl">
                                                Confirm Password
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type="password"
                                                    className="shadow-none monospace-font font-size-22 py-2"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <button
                                    type="submit"
                                    className="w-full black-button plex-sans-bold text-xl"
                                >
                                    Reset Password
                                </button>
                            </form>
                        </Form>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
